export const SEGMENT_TYPES = {
  flight:     { label: "Flight",      color: "#3B6ECC", bg: "#EEF3FC", icon: "flight" },
  hotel:      { label: "Hotel",       color: "#2C7A4B", bg: "#EBF5F0", icon: "hotel" },
  restaurant: { label: "Restaurant",  color: "#C05621", bg: "#FEF3EC", icon: "restaurant" },
  activity:   { label: "Activity",    color: "#6B46C1", bg: "#F3EEF9", icon: "activity" },
  train:      { label: "Train",       color: "#2C6B8A", bg: "#EBF4F8", icon: "train" },
  car:        { label: "Car Rental",  color: "#744210", bg: "#FEF9EC", icon: "car" },
};

export const TRIP_COLORS = [
  "#2C4A3E", "#3B6ECC", "#C05621", "#6B46C1", "#2C6B8A", "#744210"
];

const currentYear = new Date().getFullYear();

// Local-time helpers (device timezone) — used to decide what is still "upcoming"
const pad = (n) => String(n).padStart(2, "0");

export const localDateStr = (d = new Date()) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const localTimeStr = (d = new Date()) =>
  `${pad(d.getHours())}:${pad(d.getMinutes())}`;

// A segment is upcoming if it's on a later day, or today and its time hasn't passed.
// Segments with no time stay upcoming until the end of their day.
export const isUpcoming = (s, now = new Date()) => {
  const t = localDateStr(now);
  if (s.date > t) return true;
  if (s.date < t) return false;
  return !s.time || s.time >= localTimeStr(now);
};

export const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T12:00:00");
  const year = d.getFullYear();
  const base = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  // Always show year since trips span many years
  return `${base}, ${year}`;
};

export const formatDateFull = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T12:00:00");
  const year = d.getFullYear();
  const base = d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  // Show year if not current year
  return year !== currentYear ? `${base}, ${year}` : base;
};

export const groupByDate = (segments) => {
  const groups = {};
  (segments || []).forEach((s) => {
    if (!groups[s.date]) groups[s.date] = [];
    groups[s.date].push(s);
  });
  Object.keys(groups).forEach((d) =>
    groups[d].sort((a, b) => (a.time || "").localeCompare(b.time || ""))
  );
  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
};

export const inferCityFromTrip = (segments, date) => {
  if (!segments || !segments.length || !date) return null;
  const sorted = [...segments].sort((a, b) => a.date.localeCompare(b.date));
  const hotels = sorted.filter((s) => s.type === "hotel" && s.date <= date);
  if (hotels.length) {
    const hotel = hotels[hotels.length - 1];
    const addressPart = hotel.detail?.split("·")[0]?.trim() || "";
    const commaIdx = addressPart.lastIndexOf(",");
    const city = commaIdx > -1 ? addressPart.substring(commaIdx + 1).trim() : "";
    return city ? `${city} (staying at ${hotel.title})` : hotel.title;
  }
  const nextFlight = sorted.find((s) => s.type === "flight" && s.date >= date);
  if (nextFlight) {
    const dest = nextFlight.title?.split("->")[1]?.trim();
    return dest || null;
  }
  return null;
};

// Country lookup — maps address fragments to canonical country names
const COUNTRY_LOOKUP = {};
[
  [["USA","US","U.S.","U.S.A.","United States","United States of America"], "USA"],
  [["UK","U.K.","United Kingdom","Great Britain","England","Britain"], "UK"],
  [["France","FR"], "France"],
  [["Germany","DE","Deutschland"], "Germany"],
  [["Italy","IT","Italia"], "Italy"],
  [["Spain","ES","España"], "Spain"],
  [["Netherlands","NL","Nederland","Holland"], "Netherlands"],
  [["Switzerland","CH","Schweiz","Suisse"], "Switzerland"],
  [["Austria","AT","Österreich"], "Austria"],
  [["Portugal","PT"], "Portugal"],
  [["Belgium","BE","Belgique"], "Belgium"],
  [["Denmark","DK","Danmark"], "Denmark"],
  [["Sweden","SE","Sverige"], "Sweden"],
  [["Norway","NO","Norge"], "Norway"],
  [["Finland","FI","Suomi"], "Finland"],
  [["Poland","PL","Polska"], "Poland"],
  [["Czech Republic","CZ","Czechia"], "Czech Republic"],
  [["Hungary","HU"], "Hungary"],
  [["Greece","GR"], "Greece"],
  [["Croatia","HR","Hrvatska"], "Croatia"],
  [["Ireland","IE","Éire"], "Ireland"],
  [["Scotland"], "Scotland"],
  [["Japan","JP"], "Japan"],
  [["China","CN"], "China"],
  [["Hong Kong","HK"], "Hong Kong"],
  [["Singapore","SG"], "Singapore"],
  [["Thailand","TH"], "Thailand"],
  [["Australia","AU"], "Australia"],
  [["New Zealand","NZ"], "New Zealand"],
  [["Canada","CA"], "Canada"],
  [["Mexico","MX","México"], "Mexico"],
  [["Brazil","BR","Brasil"], "Brazil"],
  [["Argentina","AR"], "Argentina"],
  [["Colombia","CO"], "Colombia"],
  [["Peru","PE"], "Peru"],
  [["Chile","CL"], "Chile"],
  [["UAE","AE","United Arab Emirates"], "UAE"],
  [["Qatar","QA"], "Qatar"],
  [["Turkey","TR","Türkiye"], "Turkey"],
  [["Israel","IL"], "Israel"],
  [["Egypt","EG"], "Egypt"],
  [["Morocco","MA","Maroc"], "Morocco"],
  [["South Africa","ZA"], "South Africa"],
  [["Kenya","KE"], "Kenya"],
  [["South Korea","KR","Korea"], "South Korea"],
  [["Taiwan","TW"], "Taiwan"],
  [["Vietnam","VN"], "Vietnam"],
  [["Indonesia","ID"], "Indonesia"],
  [["Malaysia","MY"], "Malaysia"],
  [["Philippines","PH"], "Philippines"],
  [["India","IN"], "India"],
  [["Iceland","IS"], "Iceland"],
  [["Cuba","CU"], "Cuba"],
  [["Costa Rica","CR"], "Costa Rica"],
  [["Panama","PA"], "Panama"],
  [["Ecuador","EC"], "Ecuador"],
  [["Uruguay","UY"], "Uruguay"],
  [["Cambodia","KH"], "Cambodia"],
  [["Nepal","NP"], "Nepal"],
  [["Sri Lanka","LK"], "Sri Lanka"],
  [["Jordan","JO"], "Jordan"],
  [["Saudi Arabia","SA"], "Saudi Arabia"],
  [["Tanzania","TZ"], "Tanzania"],
  [["Ghana","GH"], "Ghana"],
  [["Nigeria","NG"], "Nigeria"],
  [["Tunisia","TN","Tunisie"], "Tunisia"],
  [["Romania","RO"], "Romania"],
  [["Togo","TG"], "Togo"],
  [["Senegal","SN"], "Senegal"],
  [["Côte d'Ivoire","Ivory Coast","CI"], "Côte d'Ivoire"],
].forEach(([variants, canonical]) => {
  variants.forEach(v => { COUNTRY_LOOKUP[v.toLowerCase()] = canonical; });
});

// Extract country from a formatted address string by checking the last 3 comma-parts
export const extractCountryFromAddress = (address) => {
  if (!address) return null;
  const parts = address.split(",").map(p => p.trim()).filter(Boolean);
  for (let i = parts.length - 1; i >= Math.max(0, parts.length - 3); i--) {
    const candidate = parts[i].replace(/\d+/g, "").trim();
    const match = COUNTRY_LOOKUP[candidate.toLowerCase()];
    if (match) return match;
  }
  return null;
};

// Extract city from a formatted address — typically the second-to-last meaningful part
export const extractCityFromAddress = (address) => {
  if (!address) return null;
  const parts = address.split(",").map(p => p.trim()).filter(Boolean);
  if (parts.length < 2) return null;
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i].replace(/\d+/g, "").trim();
    if (!part) continue;
    if (COUNTRY_LOOKUP[part.toLowerCase()]) continue;
    if (/^[A-Z]{2}$/.test(part)) continue;
    return parts[i];
  }
  return null;
};

export const getMapsUrl = (segment) => {
  if (!segment.location && !segment.detail) return null;
  const address = segment.detail?.split("·")[0]?.trim() || segment.title;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
};
